/**
 * Approval Center — 人在回路审批中心
 *
 * Tabs: 待我处理(pending) / 我发起的(submitted) / 全部(all)
 * 审批类型: 素材审核(蓝) / 预算审批(橙) / 达人建联(紫) / 策略确认(绿)
 */

import { useState, useEffect, useCallback } from 'react';
import { Tabs, Table, Tag, Button, Modal, Input, Badge, message, Tooltip, Empty } from 'antd';
import {
  CheckOutlined, CloseOutlined, EyeOutlined,
  SafetyOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import {
  listPending, listAll, listSubmitted,
  approve, reject, getPendingCount,
} from '../../api/approval';

const { TextArea } = Input;

// ─── 审批类型映射 ───
const APPROVAL_TYPE_MAP = {
  content_review: { label: '素材审核', color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
  budget: { label: '预算审批', color: '#f97316', bg: 'rgba(249,115,22,0.12)' },
  influencer_email: { label: '达人建联', color: '#a855f7', bg: 'rgba(168,85,247,0.12)' },
  strategy: { label: '策略确认', color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
};

// ─── 状态映射 ───
const STATUS_MAP = {
  pending: { label: '待处理', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  approved: { label: '已通过', color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
  rejected: { label: '已驳回', color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
  cancelled: { label: '已撤销', color: '#6b7280', bg: 'rgba(107,114,128,0.12)' },
};

// ─── 优先级映射 ───
const PRIORITY_MAP = {
  high: { label: '高', color: '#ef4444' },
  medium: { label: '中', color: '#f59e0b' },
  low: { label: '低', color: '#6b7280' },
};

export default function ApprovalCenter() {
  const [activeTab, setActiveTab] = useState('pending');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // 驳回弹窗
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

  // 详情弹窗
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailTarget, setDetailTarget] = useState(null);

  // ─── 加载数据 ───
  const fetchData = useCallback(async (tab) => {
    setLoading(true);
    try {
      let result;
      if (tab === 'pending') result = await listPending();
      else if (tab === 'submitted') result = await listSubmitted();
      else result = await listAll();
      setData(result);
    } catch {
      message.error('加载审批数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  // 加载待审批数量
  const fetchPendingCount = useCallback(async () => {
    try {
      const count = await getPendingCount();
      setPendingCount(count);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchData(activeTab);
  }, [activeTab, fetchData]);

  useEffect(() => {
    fetchPendingCount();
  }, [fetchPendingCount]);

  // ─── 审批通过 ───
  const handleApprove = (record) => {
    Modal.confirm({
      title: (
        <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
          确认通过审批
        </span>
      ),
      content: (
        <div style={{ marginTop: 12 }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 8 }}>
            您确定要通过以下审批流程吗？
          </p>
          <div style={{
            padding: '10px 14px',
            background: 'rgba(34,197,94,0.06)',
            borderRadius: 8,
            border: '1px solid rgba(34,197,94,0.15)',
          }}>
            <span style={{ color: 'var(--text-primary)', fontWeight: 500, fontSize: 13 }}>
              {record.taskTitle}
            </span>
            <span style={{
              marginLeft: 8,
              fontSize: 11,
              color: APPROVAL_TYPE_MAP[record.approvalType]?.color,
              padding: '2px 8px',
              borderRadius: 4,
              background: APPROVAL_TYPE_MAP[record.approvalType]?.bg,
            }}>
              {APPROVAL_TYPE_MAP[record.approvalType]?.label}
            </span>
          </div>
        </div>
      ),
      icon: <SafetyOutlined style={{ color: '#22c55e' }} />,
      okText: '确认通过',
      cancelText: '取消',
      okButtonProps: {
        style: {
          background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
          border: 'none',
          fontWeight: 600,
        },
      },
      onOk: async () => {
        try {
          await approve(record.id);
          message.success('审批已通过');
          // 乐观更新：更新状态
          setData((prev) => prev.map((item) =>
            item.id === record.id ? { ...item, status: 'approved' } : item
          ));
          fetchPendingCount();
        } catch {
          message.error('操作失败，请重试');
        }
      },
    });
  };

  // ─── 驳回 ───
  const handleRejectClick = (record) => {
    setRejectTarget(record);
    setRejectReason('');
    setRejectModalVisible(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      message.warning('请填写驳回原因');
      return;
    }
    setRejectSubmitting(true);
    try {
      await reject(rejectTarget.id, rejectReason);
      message.success('已驳回');
      setRejectModalVisible(false);
      // 乐观更新
      setData((prev) => prev.map((item) =>
        item.id === rejectTarget.id
          ? { ...item, status: 'rejected', rejectReason }
          : item
      ));
      fetchPendingCount();
    } catch {
      message.error('操作失败，请重试');
    } finally {
      setRejectSubmitting(false);
    }
  };

  // ─── 查看详情 ───
  const handleViewDetail = (record) => {
    setDetailTarget(record);
    setDetailVisible(true);
  };

  // ─── Tab 配置 ───
  const tabItems = [
    {
      key: 'pending',
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          待我处理
          {pendingCount > 0 && (
            <Badge
              count={pendingCount}
              size="small"
              style={{ background: '#ef4444' }}
            />
          )}
        </span>
      ),
    },
    { key: 'submitted', label: '我发起的' },
    { key: 'all', label: '全部' },
  ];

  // ─── Table 列 ───
  const columns = [
    {
      title: '流程',
      dataIndex: 'taskTitle',
      key: 'taskTitle',
      width: 280,
      render: (_, record) => (
        <div>
          <div style={{
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--text-primary)',
            marginBottom: 4,
          }}>
            {record.priority === 'high' && (
              <ThunderboltOutlined style={{ color: '#ef4444', marginRight: 6, fontSize: 12 }} />
            )}
            {record.taskTitle}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {record.agentName}
            </span>
            <span style={{
              fontSize: 10,
              color: 'var(--text-muted)',
              padding: '1px 6px',
              borderRadius: 3,
              background: 'var(--glass-sm)',
              border: '1px solid var(--border-subtle)',
            }}>
              {record.step}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: '类型',
      dataIndex: 'approvalType',
      key: 'approvalType',
      width: 120,
      render: (type) => {
        const meta = APPROVAL_TYPE_MAP[type];
        if (!meta) return '-';
        return (
          <span style={{
            fontSize: 12,
            fontWeight: 500,
            color: meta.color,
            padding: '3px 10px',
            borderRadius: 6,
            background: meta.bg,
            border: `1px solid ${meta.color}20`,
          }}>
            {meta.label}
          </span>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const meta = STATUS_MAP[status];
        if (!meta) return '-';
        return (
          <span style={{
            fontSize: 12,
            fontWeight: 500,
            color: meta.color,
            padding: '3px 10px',
            borderRadius: 6,
            background: meta.bg,
          }}>
            {meta.label}
          </span>
        );
      },
    },
    {
      title: '提交人',
      dataIndex: 'submitter',
      key: 'submitter',
      width: 130,
      render: (submitter) => (
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {submitter}
        </span>
      ),
    },
    {
      title: '提交时间',
      dataIndex: 'submitTime',
      key: 'submitTime',
      width: 160,
      render: (time) => (
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {time}
        </span>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 6 }}>
          {record.status === 'pending' ? (
            <>
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                onClick={() => handleApprove(record)}
                style={{
                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  border: 'none',
                  fontWeight: 500,
                  fontSize: 12,
                }}
              >
                通过
              </Button>
              <Button
                danger
                size="small"
                icon={<CloseOutlined />}
                onClick={() => handleRejectClick(record)}
                style={{ fontWeight: 500, fontSize: 12 }}
              >
                驳回
              </Button>
            </>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              -
            </span>
          )}
          <Tooltip title="查看详情">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
              style={{
                fontSize: 12,
                color: 'var(--text-secondary)',
                borderColor: 'var(--border-default)',
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  // ─── 非 pending tab 的操作列只保留详情 ───
  const filteredColumns = activeTab === 'pending'
    ? columns
    : columns.map((col) => {
        if (col.key === 'actions') {
          return {
            ...col,
            width: 100,
            render: (_, record) => (
              <Tooltip title="查看详情">
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => handleViewDetail(record)}
                  style={{
                    fontSize: 12,
                    color: 'var(--text-secondary)',
                    borderColor: 'var(--border-default)',
                  }}
                />
              </Tooltip>
            ),
          };
        }
        return col;
      });

  return (
    <div style={styles.pageShell}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .approval-table .ant-table {
          background: transparent;
        }
        .approval-table .ant-table-thead > tr > th {
          background: var(--surface-1) !important;
          color: var(--text-muted) !important;
          font-size: 12px;
          font-weight: 500;
          border-bottom: 1px solid var(--border-subtle) !important;
          padding: 12px 16px !important;
        }
        .approval-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid var(--border-subtle) !important;
          padding: 14px 16px !important;
          background: transparent !important;
          color: var(--text-primary);
        }
        .approval-table .ant-table-tbody > tr:hover > td {
          background: var(--accent-soft) !important;
        }
        .approval-table .ant-table-placeholder {
          background: transparent !important;
        }
        .approval-tabs .ant-tabs-tab {
          color: var(--text-muted) !important;
          font-size: 13px;
          font-weight: 500;
          padding: 8px 16px !important;
        }
        .approval-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
          color: var(--accent) !important;
        }
        .approval-tabs .ant-tabs-ink-bar {
          background: var(--accent) !important;
        }
      `}</style>

      {/* ── Header ── */}
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>
            <SafetyOutlined style={{ marginRight: 12, fontSize: 24, color: 'var(--accent)' }} />
            审批中心
          </h1>
          <p style={styles.subtitle}>
            人在回路 -- 审批 Agent 发起的关键决策与物料
          </p>
        </div>
        <div style={styles.statsCard}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>待审批</span>
          <span style={{
            fontSize: 28,
            fontWeight: 700,
            color: pendingCount > 0 ? '#ef4444' : 'var(--text-muted)',
          }}>
            {pendingCount}
          </span>
        </div>
      </header>

      {/* ── Tabs + Table ── */}
      <div style={styles.tableCard}>
        <Tabs
          className="approval-tabs"
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          style={{ marginBottom: 0 }}
        />

        <Table
          className="approval-table"
          columns={filteredColumns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showTotal: (total) => `共 ${total} 条`,
            style: { marginTop: 16, padding: '0 16px' },
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    暂无审批数据
                  </span>
                }
              />
            ),
          }}
          style={{ background: 'transparent' }}
        />
      </div>

      {/* ── 驳回弹窗 ── */}
      <Modal
        title={
          <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
            <CloseOutlined style={{ color: '#ef4444', marginRight: 8 }} />
            驳回审批
          </span>
        }
        open={rejectModalVisible}
        onCancel={() => setRejectModalVisible(false)}
        onOk={handleRejectSubmit}
        confirmLoading={rejectSubmitting}
        okText="确认驳回"
        cancelText="取消"
        okButtonProps={{
          danger: true,
          style: { fontWeight: 600 },
        }}
        destroyOnClose
      >
        <div style={{ marginTop: 16 }}>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
            正在驳回: <strong style={{ color: 'var(--text-primary)' }}>{rejectTarget?.taskTitle}</strong>
          </p>
          <div style={{
            padding: '10px 14px',
            background: 'rgba(239,68,68,0.06)',
            borderRadius: 8,
            border: '1px solid rgba(239,68,68,0.12)',
            marginBottom: 16,
          }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
              类型
            </span>
            <span style={{
              fontSize: 12,
              fontWeight: 500,
              color: APPROVAL_TYPE_MAP[rejectTarget?.approvalType]?.color,
            }}>
              {APPROVAL_TYPE_MAP[rejectTarget?.approvalType]?.label}
            </span>
          </div>
          <label style={{ fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 8 }}>
            驳回原因 <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <TextArea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="请详细说明驳回原因，以便发起方修改后重新提交..."
            rows={4}
            style={{
              background: 'var(--glass-sm)',
              border: '1px solid var(--border-default)',
              color: 'var(--text-primary)',
              fontSize: 13,
            }}
          />
          {!rejectReason && rejectReason !== '' && (
            <span style={{ fontSize: 11, color: '#ef4444', marginTop: 4, display: 'block' }}>
              请填写驳回原因
            </span>
          )}
        </div>
      </Modal>

      {/* ── 详情弹窗 ── */}
      <Modal
        title={
          <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
            <EyeOutlined style={{ color: 'var(--accent)', marginRight: 8 }} />
            审批详情
          </span>
        }
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailVisible(false)}>
            关闭
          </Button>,
        ]}
        destroyOnClose
      >
        {detailTarget && (
          <div style={{ marginTop: 16 }}>
            <DetailRow label="流程标题" value={detailTarget.taskTitle} />
            <DetailRow label="审批类型">
              <span style={{
                fontSize: 12,
                fontWeight: 500,
                color: APPROVAL_TYPE_MAP[detailTarget.approvalType]?.color,
                padding: '3px 10px',
                borderRadius: 6,
                background: APPROVAL_TYPE_MAP[detailTarget.approvalType]?.bg,
              }}>
                {APPROVAL_TYPE_MAP[detailTarget.approvalType]?.label}
              </span>
            </DetailRow>
            <DetailRow label="当前状态">
              <span style={{
                fontSize: 12,
                fontWeight: 500,
                color: STATUS_MAP[detailTarget.status]?.color,
                padding: '3px 10px',
                borderRadius: 6,
                background: STATUS_MAP[detailTarget.status]?.bg,
              }}>
                {STATUS_MAP[detailTarget.status]?.label}
              </span>
            </DetailRow>
            <DetailRow label="优先级">
              <span style={{
                fontSize: 12,
                color: PRIORITY_MAP[detailTarget.priority]?.color,
              }}>
                {PRIORITY_MAP[detailTarget.priority]?.label}
              </span>
            </DetailRow>
            <DetailRow label="Agent" value={detailTarget.agentName} />
            <DetailRow label="审批步骤" value={detailTarget.step} />
            <DetailRow label="提交人" value={detailTarget.submitter} />
            <DetailRow label="提交时间" value={detailTarget.submitTime} />
            <DetailRow label="描述" value={detailTarget.description} />
            {detailTarget.rejectReason && (
              <DetailRow label="驳回原因">
                <span style={{ color: '#ef4444', fontSize: 13 }}>
                  {detailTarget.rejectReason}
                </span>
              </DetailRow>
            )}
            {detailTarget.attachments && detailTarget.attachments.length > 0 && (
              <DetailRow label="附件">
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {detailTarget.attachments.map((att, i) => (
                    <span key={i} style={{
                      fontSize: 11,
                      padding: '3px 10px',
                      borderRadius: 4,
                      background: 'var(--glass-sm)',
                      border: '1px solid var(--border-subtle)',
                      color: 'var(--text-muted)',
                    }}>
                      {att}
                    </span>
                  ))}
                </div>
              </DetailRow>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ─── 详情行组件 ───
function DetailRow({ label, value, children }) {
  return (
    <div style={{
      display: 'flex',
      padding: '10px 0',
      borderBottom: '1px solid var(--border-subtle)',
    }}>
      <span style={{
        width: 80,
        flexShrink: 0,
        fontSize: 12,
        color: 'var(--text-muted)',
      }}>
        {label}
      </span>
      <span style={{
        flex: 1,
        fontSize: 13,
        color: 'var(--text-primary)',
      }}>
        {value || children}
      </span>
    </div>
  );
}

// ─── Styles ───
const styles = {
  pageShell: {
    minHeight: '100vh',
    /* 透出 #root::before 的森林背景（登录页原图） */
    background: 'transparent',
    padding: '32px 36px 80px',
    fontFamily: 'var(--font-sans)',
    color: 'var(--text-primary)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 28,
    animation: 'fadeUp var(--duration-slow) var(--ease) both',
  },
  title: {
    fontSize: 26,
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  },
  subtitle: {
    fontSize: 13,
    color: 'var(--text-muted)',
    marginTop: 6,
    letterSpacing: '0.04em',
  },
  statsCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    padding: '16px 24px',
    background: 'var(--surface-1)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    minWidth: 100,
  },
  tableCard: {
    background: 'var(--surface-1)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    padding: '8px 0 16px',
    animation: 'fadeUp var(--duration-slow) var(--ease) 100ms both',
  },
};
